
import { DetectionResult } from '../types';

// Enhanced client-side analysis of image metadata and properties
export async function analyzeImageMetadata(imageFile: File): Promise<Partial<DetectionResult>> {
  return new Promise((resolve) => {
    const flags: string[] = [];
    let score = 0;
    
    try {
      // Check file extension
      const fileExtension = imageFile.name.split('.').pop()?.toLowerCase();
      const isCommonFormat = ['jpg', 'jpeg', 'png', 'webp'].includes(fileExtension || '');
      
      if (!isCommonFormat) {
        flags.push("Unusual file format detected");
        score += 0.1;
      }
      
      // Check file size
      const fileSizeKB = imageFile.size / 1024;
      
      if (fileSizeKB < 10) {
        flags.push("Unusually small file size");
        score += 0.15;
      }
      
      if (fileSizeKB > 15000) {
        flags.push("Extremely large file size");
        score += 0.05;
      }
      
      // Create an image object to analyze dimensions
      const img = new Image();
      const objectURL = URL.createObjectURL(imageFile);
      
      img.onload = () => {
        // Check image dimensions
        if (img.width < 200 || img.height < 200) {
          flags.push("Low resolution image");
          score += 0.2;
        }
        
        // Check for unusual aspect ratios (often found in AI-generated images)
        const aspectRatio = img.width / img.height;
        if (aspectRatio === 1) {
          flags.push("Perfect square aspect ratio (common in AI-generated images)");
          score += 0.15;
        } else if (aspectRatio > 3 || aspectRatio < 0.33) {
          flags.push("Unusual aspect ratio");
          score += 0.1;
        }
        
        // Revoke object URL to prevent memory leaks
        URL.revokeObjectURL(objectURL);
        
        // Generate explanation based on findings
        let explanation = "Metadata analysis complete. ";
        if (flags.length > 0) {
          explanation += "Some anomalies were detected in the image properties.";
        } else {
          explanation += "No obvious anomalies detected in basic image properties.";
        }
        
        resolve({
          flags,
          score: Math.min(0.85, score), // Cap score at 0.85
          explanation,
          confidence: 0.6, // Medium confidence in metadata-only analysis
        });
      };
      
      img.onerror = () => {
        flags.push("Error loading image data");
        score += 0.3;
        
        resolve({
          flags,
          score: Math.min(0.85, score),
          explanation: "Error analyzing image data. The file may be corrupted or in an unsupported format.",
          confidence: 0.4,
        });
      };
      
      img.src = objectURL;
    } catch (e) {
      console.error("Error in metadata analysis:", e);
      flags.push("Error during metadata analysis");
      
      resolve({
        flags,
        score: 0.3,
        explanation: "An error occurred during metadata analysis.",
        confidence: 0.3,
      });
    }
  });
}

// Enhanced client-side analysis with canvas-based techniques
export async function enhancedClientSideAnalysis(imageFile: File, metadataAnalysis: Partial<DetectionResult>): Promise<Partial<DetectionResult>> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectURL = URL.createObjectURL(imageFile);
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error("Could not get canvas context");
        }
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Analyze noise patterns (simplified)
        const noiseAnalysis = analyzeImageNoise(data, canvas.width, canvas.height);
        
        // Combine with metadata analysis
        const combinedFlags = [...(metadataAnalysis.flags || []), ...noiseAnalysis.flags];
        const combinedScore = Math.max(metadataAnalysis.score || 0, noiseAnalysis.score);
        
        URL.revokeObjectURL(objectURL);
        
        resolve({
          flags: combinedFlags,
          score: combinedScore,
          explanation: "Enhanced client-side analysis performed, including noise pattern detection and histogram analysis.",
          confidence: 0.7,
        });
      } catch (e) {
        console.error("Error in enhanced analysis:", e);
        URL.revokeObjectURL(objectURL);
        
        resolve({
          flags: ["Error during enhanced analysis"],
          score: metadataAnalysis.score || 0.3,
          explanation: "An error occurred during enhanced image analysis.",
          confidence: 0.5,
        });
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectURL);
      resolve(metadataAnalysis); // Fall back to metadata analysis
    };
    
    img.src = objectURL;
  });
}

// Analyze image noise patterns (simplified)
export function analyzeImageNoise(data: Uint8ClampedArray, width: number, height: number): { flags: string[], score: number } {
  const flags: string[] = [];
  let score = 0;
  
  try {
    // Calculate histogram
    const redHistogram = new Array(256).fill(0);
    const greenHistogram = new Array(256).fill(0);
    const blueHistogram = new Array(256).fill(0);
    
    for (let i = 0; i < data.length; i += 4) {
      redHistogram[data[i]]++;
      greenHistogram[data[i + 1]]++;
      blueHistogram[data[i + 2]]++;
    }
    
    // Check for unusual histogram patterns (over-simplified)
    const redZeros = redHistogram.filter(x => x === 0).length;
    const greenZeros = greenHistogram.filter(x => x === 0).length;
    const blueZeros = blueHistogram.filter(x => x === 0).length;
    
    const totalPixels = width * height;
    const redPeaks = findHistogramPeaks(redHistogram, totalPixels);
    const greenPeaks = findHistogramPeaks(greenHistogram, totalPixels);
    const bluePeaks = findHistogramPeaks(blueHistogram, totalPixels);
    
    // Unusual histogram patterns
    if (redZeros > 150 || greenZeros > 150 || blueZeros > 150) {
      flags.push("Unusual color distribution detected");
      score += 0.2;
    }
    
    // Unusual peaks in histograms
    if (redPeaks < 4 || redPeaks > 50 || greenPeaks < 4 || greenPeaks > 50 || bluePeaks < 4 || bluePeaks > 50) {
      flags.push("Atypical color patterns detected");
      score += 0.25;
    }
    
    return { flags, score: Math.min(0.75, score) };
  } catch (e) {
    console.error("Error analyzing noise patterns:", e);
    return { flags: ["Error during noise analysis"], score: 0.15 };
  }
}

// Find peaks in a histogram
export function findHistogramPeaks(histogram: number[], totalPixels: number): number {
  let peaks = 0;
  const threshold = totalPixels * 0.001; // Minimum count to be considered a peak
  
  for (let i = 1; i < 255; i++) {
    if (histogram[i] > threshold && histogram[i] > histogram[i - 1] && histogram[i] > histogram[i + 1]) {
      peaks++;
    }
  }
  
  return peaks;
}

// Convert File to base64
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

// Fallback image analysis when everything else fails
export function fallbackImageAnalysis(imageFile: File): DetectionResult {
  const fileSize = imageFile.size / 1024; // KB
  const fileExtension = imageFile.name.split('.').pop()?.toLowerCase();
  
  // Adjust score based on file properties
  let baseScore = 0.3;
  if (!['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(fileExtension || '')) {
    baseScore += 0.1;
  }
  if (fileSize < 20) {
    baseScore += 0.15;
  }
  if (fileSize > 10000) {
    baseScore += 0.05;
  }
  
  return {
    score: baseScore,
    confidence: 0.5,
    flags: ["Limited analysis performed due to technical limitations"],
    explanation: "Unable to perform complete analysis. Basic file properties were examined, but more sophisticated detection was unavailable.",
    modelName: "Fallback Analysis",
    timestamp: new Date()
  };
}
